import React, { useEffect, useMemo } from 'react';
import { Platform, NativeModules } from 'react-native';
import { USER_API_BASE_URL } from '../../api/constant';

// Optional import for device-only Stripe Terminal module
let StripeTerminalProvider: any = null;
let StripeTerminalContext: any = null;
let StripeTerminalAvailable = false;

if (Platform.OS !== 'web') {
  try {
    const stripeTerminalModule = require('@stripe/stripe-terminal-react-native');
    // Handle both named and default exports
    StripeTerminalProvider = stripeTerminalModule.StripeTerminalProvider || stripeTerminalModule.default?.StripeTerminalProvider;
    StripeTerminalContext = stripeTerminalModule.StripeTerminalContext || stripeTerminalModule.default?.StripeTerminalContext;

    if (StripeTerminalProvider) {
      const nativeModule = NativeModules.StripeTerminalReactNative;
      if (nativeModule && (typeof nativeModule.setConnectionToken === 'function' || typeof nativeModule.initialize === 'function')) {
        StripeTerminalAvailable = true;
        if (__DEV__) console.log('✅ [Stripe Terminal] Native module detected');
      } else {
        if (__DEV__) console.warn('⚠️ [Stripe Terminal] JS module found but native module not functional');
        StripeTerminalAvailable = false;
      }
    }
  } catch (e: any) {
    if (__DEV__) console.warn('⚠️ [Stripe Terminal] Module import failed:', e.message);
    StripeTerminalAvailable = false;
  }
}

const fetchTokenProvider = () => {
  if (!StripeTerminalAvailable) return Promise.resolve('dummy_token_not_available');

  return fetch(`${USER_API_BASE_URL}/stripe/connection_token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch connection token: ${response.status}`);
      return response.json();
    })
    .then((data) => data.secret)
    .catch((error) => {
      if (__DEV__) console.warn('⚠️ [Stripe Terminal] Failed to fetch connection token:', error?.message || error);
      return 'dummy_token_error_fallback';
    });
};

export const SafeStripeTerminalProvider = ({ children, ...props }: any) => {
  // Define the mock context value
  const mockContextValue = useMemo(() => ({
    loading: false,
    isInitialized: false,
    getIsInitialized: () => false,
    discoveredReaders: [],
    connectedReader: null,
    log: (code: string, msg: string) => { if (__DEV__) console.log(`[Stripe Terminal Mock]: ${code}`, msg); },
    setLoading: () => {},
    setConnectedReader: () => {},
    setDiscoveredReaders: () => {},
    initialize: async () => {
      const msg = 'Stripe Terminal is not available in this environment (Mock)';
      if (__DEV__) console.warn(`⚠️ ${msg}`);
      return { error: { code: 'Failed', message: msg } };
    },
    discoverReaders: async ({ simulated }: any) => {
      if (simulated) {
        // Return a mock reader after a small delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        return { error: undefined };
      }
      return { error: { code: 'Failed', message: 'Not available' } };
    },
    connectReader: async () => ({ error: { code: 'Failed', message: 'Not available' } }),
    disconnectReader: async () => ({ error: undefined }),
    emitter: undefined,
  }), []);

  useEffect(() => {
    if (__DEV__) {
      console.log('[SafeStripeTerminalProvider] Mounted', {
        available: StripeTerminalAvailable,
        hasProvider: !!StripeTerminalProvider,
        hasContext: !!StripeTerminalContext
      });
    }
  }, []);

  // On web, just return children
  if (Platform.OS === 'web') {
    return children;
  }

  // If we have the real provider, use it
  if (StripeTerminalProvider) {
    const safeTokenProvider = async () => {
      try {
        return await fetchTokenProvider();
      } catch (error: any) {
        if (__DEV__) console.warn('⚠️ [Stripe Terminal] Token provider error:', error?.message || error);
        return 'dummy_token_error_fallback';
      }
    };

    try {
      return (
        <StripeTerminalProvider {...props} tokenProvider={safeTokenProvider}>
          {children}
        </StripeTerminalProvider>
      );
    } catch (error) {
      if (__DEV__) console.error('❌ [Stripe Terminal] Provider failed to render:', error);
    }
  }

  // Fallback to mock context if context exists
  if (StripeTerminalContext) {
    return (
      <StripeTerminalContext.Provider value={mockContextValue}>
        {children}
      </StripeTerminalContext.Provider>
    );
  }

  // Final fallback
  if (__DEV__) console.error('❌ [Stripe Terminal] NO PROVIDER OR CONTEXT AVAILABLE');
  return children;
};
